import { useEffect, useRef, useState, type JSX } from 'react';
import { renderMarkdown } from './markdown';

/**
 * ResponseBody — the single component that renders assistant prose.
 *
 * Every surface that shows a model answer goes through here (the chat message,
 * the Deep Search card, anything added later), so the typography, spacing
 * rhythm, and semantic block treatment defined on `.msg__body` are identical
 * everywhere. Before this existed, each surface called `renderMarkdown` itself
 * and pasted its own `<div className="msg__body">` — the same output rendered
 * from two places, free to drift.
 *
 * It owns two things markdown alone cannot: re-parse cadence, and tail
 * stability while streaming.
 */

/**
 * Trim a TRAILING, still-incomplete markdown construct off a streaming buffer.
 *
 * Why: tokens arrive mid-syntax. For a few frames the buffer ends "…the **key"
 * — real markdown for which the correct render is a literal `**`. So the reader
 * watches asterisks appear, sit there, and then vanish as the closer lands and
 * the text reflows into bold. The same happens with `` ` ``, a half-typed link,
 * and a just-opened fence, which pops a full code panel open before it has any
 * content. None of it is wrong, all of it is visual noise during the one moment
 * the user is actively reading.
 *
 * Holding the incomplete tail back for a few hundred milliseconds costs nothing
 * — the very next parse includes it — and keeps the text stable as it grows.
 * Applied ONLY while streaming; the settled render always shows everything.
 */
export function stabilizeStreamingTail(src: string): string {
  if (!src) return src;

  // 1. A fence that has opened but not closed. Drop it only while it is still
  //    EMPTY — an opener with no lines yet pops a bordered panel open and then
  //    reflows. Once it has content the panel is genuinely there and filling in,
  //    which reads as progress, so leave it alone.
  const fences = src.match(/^[ \t]*(?:`{3,}|~{3,})/gm);
  if (fences && fences.length % 2 === 1) {
    const open = src.lastIndexOf(fences[fences.length - 1] ?? '```');
    const after = src.slice(open).split('\n').slice(1).join('\n');
    return after.trim().length === 0 ? src.slice(0, open).trimEnd() : src;
  }

  // 2. Unclosed inline constructs, scoped to the LINE currently being written.
  //    Scoping matters: counting delimiters across the whole message would let
  //    one stray backtick in already-settled text cut away a paragraph the user
  //    is reading. Any mistake here can only ever affect the final line, for one
  //    150ms frame. Parity — not a trailing-match regex — decides "unclosed", so
  //    a COMPLETED `**bold**` is never mistaken for an open one.
  const cut = src.lastIndexOf('\n') + 1;
  const head = src.slice(0, cut);
  let tail = src.slice(cut);

  const count = (needle: string): number => tail.split(needle).length - 1;

  if (count('`') % 2 === 1) tail = tail.slice(0, tail.lastIndexOf('`'));
  if (count('**') % 2 === 1) tail = tail.slice(0, tail.lastIndexOf('**'));
  const openBracket = tail.lastIndexOf('[');
  if (openBracket > tail.lastIndexOf(']')) {
    tail = tail.slice(0, openBracket);                 // "see [the doc" → "see "
  } else if (tail.lastIndexOf('](') > tail.lastIndexOf(')')) {
    const label = tail.lastIndexOf('[');               // "[label](http" → drop the whole link
    tail = tail.slice(0, label >= 0 ? label : tail.lastIndexOf(']('));
  }

  return head + tail;
}

interface ResponseBodyProps {
  /** Raw markdown from the model. */
  body: string;
  /** True while tokens are still arriving for THIS message. */
  streaming?: boolean;
  /** Extra classes on the body element (callers keep their own modifiers). */
  className?: string;
}

/**
 * Renders a message's markdown body, THROTTLING the expensive re-parse while the
 * reply is still streaming. Before this, `renderMarkdown` ran on EVERY token over
 * the whole growing message — O(n²) work that froze the main thread on long outputs
 * (a full PDF's worth of text) → the browser's "This page isn't responding". Now,
 * while streaming, we re-parse from the LATEST body on a fixed ~150ms cadence
 * (decoupled from token rate); when the stream settles we do one final full render.
 * Formatting still streams live — just a few times/second instead of per-token.
 */
export function ResponseBody({ body, streaming = false, className }: ResponseBodyProps): JSX.Element {
  const [html, setHtml] = useState<string>(() => renderMarkdown(body));
  const bodyRef = useRef(body);
  bodyRef.current = body;

  useEffect(() => {
    if (!streaming) {
      // Stream finished (or a historical message) → render the complete markdown
      // once, with NO tail trimming so nothing is ever withheld from a final answer.
      setHtml(renderMarkdown(bodyRef.current));
      return undefined;
    }
    const tick = (): void => setHtml(renderMarkdown(stabilizeStreamingTail(bodyRef.current)));
    tick(); // paint the first chunk immediately rather than after one full interval
    const id = window.setInterval(tick, 150);
    return () => window.clearInterval(id);
  }, [streaming]);

  return (
    <div
      className={className ? `msg__body ${className}` : 'msg__body'}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}
